import { Extension } from '@tiptap/core';
import type { Editor, Range } from '@tiptap/core';
// TipTap Extension wrapping @tiptap/suggestion to reproduce the pre-TipTap
// slash-command palette (see slash-command-palette.tsx) — trigger char "/",
// same command list, rendered via the same React component. Positioning is
// manual (via `clientRect`), matching the app's existing floating-toolbar.tsx
// pattern, so this doesn't need to pull in tippy.js as a dependency.
import { ReactRenderer } from '@tiptap/react';
import Suggestion from '@tiptap/suggestion';
import type { SuggestionProps } from '@tiptap/suggestion';
import { SlashCommandPalette } from './slash-command-palette';

export interface SlashCommandItem {
  label: string;
  description: string;
  icon: string;
  run: (editor: Editor, range: Range) => void;
}

const ITEMS: SlashCommandItem[] = [
  {
    label: 'Texte',
    description: 'Paragraphe normal',
    icon: 'notes',
    run: (editor, range) => editor.chain().focus().deleteRange(range).setParagraph().run(),
  },
  {
    label: 'Titre 1',
    description: 'Grand titre de section',
    icon: 'title',
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 1 }).run(),
  },
  {
    label: 'Titre 2',
    description: 'Titre de section',
    icon: 'format_h2',
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 2 }).run(),
  },
  {
    label: 'Titre 3',
    description: 'Sous-titre',
    icon: 'format_h3',
    run: (editor, range) =>
      editor.chain().focus().deleteRange(range).setNode('heading', { level: 3 }).run(),
  },
  {
    label: 'Séparateur',
    description: 'Ligne horizontale',
    icon: 'horizontal_rule',
    run: (editor, range) => editor.chain().focus().deleteRange(range).setHorizontalRule().run(),
  },
  {
    label: 'Code',
    description: 'Bloc de code monospace',
    icon: 'code',
    run: (editor, range) => editor.chain().focus().deleteRange(range).setCodeBlock().run(),
  },
  {
    label: 'Liste à puces',
    description: 'Liste non ordonnée',
    icon: 'format_list_bulleted',
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleBulletList().run(),
  },
  {
    label: 'Liste numérotée',
    description: 'Liste ordonnée',
    icon: 'format_list_numbered',
    run: (editor, range) => editor.chain().focus().deleteRange(range).toggleOrderedList().run(),
  },
];

function filterItems(query: string): SlashCommandItem[] {
  const q = query.toLowerCase();
  return ITEMS.filter(
    (item) => item.label.toLowerCase().includes(q) || item.description.toLowerCase().includes(q),
  );
}

export const SlashCommand = Extension.create({
  name: 'slashCommand',

  addOptions() {
    return {
      suggestion: {
        char: '/',
        startOfLine: false,
        allowSpaces: false,
        items: ({ query }: { query: string }) => filterItems(query),
        command: ({
          editor,
          range,
          props,
        }: { editor: Editor; range: Range; props: SlashCommandItem }) => {
          props.run(editor, range);
        },
        render: () => {
          let component: ReactRenderer<
            unknown,
            {
              items: SlashCommandItem[];
              onSelect: (item: SlashCommandItem) => void;
              onClose: () => void;
            }
          > | null = null;
          let popup: HTMLElement | null = null;

          function position(rect: DOMRect | null) {
            if (!popup || !rect) return;
            popup.style.left = `${rect.left}px`;
            popup.style.top = `${rect.bottom + 4}px`;
          }

          return {
            onStart: (props: SuggestionProps<SlashCommandItem>) => {
              component = new ReactRenderer(SlashCommandPalette, {
                props: {
                  items: props.items as SlashCommandItem[],
                  onSelect: (item: SlashCommandItem) => props.command(item as never),
                  onClose: () => props.editor.commands.focus(),
                },
                editor: props.editor,
              });
              popup = component.element as HTMLElement;
              popup.style.position = 'fixed';
              popup.style.zIndex = '9999';
              document.body.appendChild(popup);
              position(props.clientRect?.() ?? null);
            },
            onUpdate: (props: SuggestionProps<SlashCommandItem>) => {
              component?.updateProps({
                items: props.items as SlashCommandItem[],
                onSelect: (item: SlashCommandItem) => props.command(item as never),
                onClose: () => props.editor.commands.focus(),
              });
              position(props.clientRect?.() ?? null);
            },
            onKeyDown: () => false, // the palette intercepts navigation keys itself (capture phase, see slash-command-palette.tsx)
            onExit: () => {
              popup?.remove();
              component?.destroy();
              popup = null;
              component = null;
            },
          };
        },
      },
    };
  },

  addProseMirrorPlugins() {
    return [
      Suggestion({
        editor: this.editor,
        ...this.options.suggestion,
      }),
    ];
  },
});
